// External libraries
import React from 'react';
import { useStoreActions, useStoreState } from '@/store';

// Internal modules
import {
  CI,
  features,
  getContent,
  getExperiment,
  isUserActionCommit,
  noop,
  redirectToLogIn,
} from '@/lib';
import { Button } from '@/components/Button';
// @ts-expect-error untyped import
import { payNowSubmitHandler } from '@/components/Form/handlers';
import {
  logFptiForHiddenButtonSubmit,
  logFptiForLoginButtonClick,
} from './fpti';
import type { ButtonClickEvent, Noop } from '@/types';
import type { ButtonProps } from '@/components/Button';
import {
  atomicsCi,
  atomicsDomain,
  atomicsFlows,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';

const endFormUserInputCi = () =>
  CI.endWith(CI.Event.GUEST_XO_SIGNUP_FORM_USER_INPUT, CI.Status.Ok);

export const SignupSubmitButton = ({
  type,
  ...props
}: ButtonProps): JSX.Element => {
  const formActions = useStoreActions(s => s.form);
  const isExistingAccount = useStoreState(({ api }) => api.isExistingAccount);

  let contentKey = getExperiment('passwordlessContentKey') || '';

  const handleClick: ButtonClickEvent = e => {
    endFormUserInputCi();
    formActions.updateOnboardOption('signup');
    payNowSubmitHandler(e);
  };

  const handleHiddenSubmit = () => {
    formActions.updateOnboardOption('signup');
  };

  if (features.isActive('isMemberMatchNoOptIn') && isExistingAccount) {
    contentKey = 'buttons.guest.base';
  }

  return (
    <>
      <Button
        customerInteraction={CI.Event.CREATE_ACCOUNT_AND_PAY_SUBMIT}
        contentKey={contentKey}
        isFormSubmission
        testID="submit-button"
        onClick={handleClick}
        type="button"
        {...props}
        atomicEventsAttributes={{
          domain: atomicsDomain,
          flow: atomicsFlows.guestAndSignup,
          intent: atomicsCi.clickCreateAccountAndContinueButton,
          task: atomicsTasks.selectCreateAccountAndContinue,
          viewName: atomicsViewNames.ryp,
        }}
      >
        {getContent(contentKey)}
      </Button>
      {type === 'submit' && <HiddenSubmitButton onClick={handleHiddenSubmit} />}
    </>
  );
};

export const GuestSubmitButton = ({
  type,
  ...props
}: ButtonProps): JSX.Element => {
  const formActions = useStoreActions(s => s.form);
  const isCommit = isUserActionCommit();

  const handleClick: ButtonClickEvent = e => {
    endFormUserInputCi();
    formActions.updateOnboardOption('guest');
    payNowSubmitHandler(e);
  };

  const handleHiddenSubmit = () => {
    formActions.updateOnboardOption('guest');
  };

  const isPOMAEligible = features.isActive('isPOMAEligible');
  const isWps = features.isActive('isWps');
  const isPOMAWps = isPOMAEligible && isWps;
  const payAsGuest = isPOMAWps || isCommit;

  const contentKey = payAsGuest
    ? 'buttons.poma.payAsGuest'
    : 'buttons.poma.continueAsGuest';

  return (
    <>
      <Button
        customerInteraction={CI.Event.GUEST_PAY_SUBMIT}
        contentKey={contentKey}
        isFormSubmission
        testID="pomaGuestSubmitButton"
        onClick={handleClick}
        type="button"
        tertiary={true}
        {...props}
        atomicEventsAttributes={{
          domain: atomicsDomain,
          flow: atomicsFlows.guestAndSignup,
          intent: atomicsCi.clickContinueAsGuestButton,
          task: atomicsTasks.selectContinueAsGuest,
          viewName: atomicsViewNames.ryp,
        }}
      >
        {getContent(contentKey)}
      </Button>
      {type === 'submit' && <HiddenSubmitButton onClick={handleHiddenSubmit} />}
    </>
  );
};

interface HiddenSubmitButtonProps {
  /** function called when form is submitted via ENTER key */
  onClick?: Noop;
}

/** Captures ENTER keypress events in form fields and submits the form */
export const HiddenSubmitButton = ({
  onClick = noop,
}: HiddenSubmitButtonProps): JSX.Element => {
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);

  // This click handler will trigger when
  // form is submitted via ENTER keypress
  const handleClick = () => {
    onClick();
    endFormUserInputCi();
    logFptiForHiddenButtonSubmit();
  };

  return (
    <input
      data-testid="HiddenSubmitButton"
      disabled={isFormSubmitting}
      onClick={handleClick}
      style={{ display: 'none' }}
      type="submit"
    />
  );
};

export const LoginAndContinueButton = (props: ButtonProps): JSX.Element => {
  const { toggleIsPageLoading } = useStoreActions(({ ui }) => ui);

  const handleLoginButtonClick = () => {
    endFormUserInputCi();
    logFptiForLoginButtonClick();
    toggleIsPageLoading(true);
    redirectToLogIn();
  };

  return (
    <Button
      testID="LoginAndContinue"
      onClick={handleLoginButtonClick}
      {...props}
    >
      {getContent('buttons.logInAndContinue')}
    </Button>
  );
};
